/**
 * Download Page - Desktop Application Download
 * 
 * Provides download links for Windows, macOS, and Linux desktop apps.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Monitor, Apple, Terminal, ArrowLeft, Shield, Zap, Wifi, HardDrive } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SyncLogoWithText } from '@/components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LATEST_VERSION = '1.0.0';

const platforms = [
  {
    id: 'windows',
    name: 'Windows',
    icon: Monitor,
    extension: '.msi',
    filename: `SYNC_${LATEST_VERSION}_x64.msi`,
    requirements: 'Windows 10 or later (64-bit)',
    size: '~15 MB',
  },
  {
    id: 'macos',
    name: 'macOS',
    icon: Apple,
    extension: '.dmg',
    filename: `SYNC_${LATEST_VERSION}_x64.dmg`,
    requirements: 'macOS 10.13 (High Sierra) or later',
    size: '~12 MB',
  },
  {
    id: 'linux',
    name: 'Linux',
    icon: Terminal,
    extension: '.AppImage',
    filename: `SYNC_${LATEST_VERSION}_amd64.AppImage`,
    requirements: 'Ubuntu 18.04+ or equivalent',
    size: '~14 MB',
  },
];

const features = [
  {
    icon: Zap,
    title: 'Lower Latency',
    description: 'Direct WebSocket connections to exchanges from your machine',
  },
  {
    icon: Shield,
    title: 'Secure',
    description: 'API keys encrypted locally, never sent to our servers',
  },
  {
    icon: Wifi,
    title: 'Your IP',
    description: 'Use exchanges that restrict by IP without server whitelisting',
  },
  {
    icon: HardDrive,
    title: 'Offline Mode',
    description: 'Paper trade without internet connection',
  },
];

export default function DownloadPage() {
  const navigate = useNavigate();
  const [detectedPlatform, setDetectedPlatform] = useState(null);
  const [latestRelease, setLatestRelease] = useState(null);

  useEffect(() => {
    // Detect user's platform
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('win')) {
      setDetectedPlatform('windows');
    } else if (userAgent.includes('mac')) {
      setDetectedPlatform('macos');
    } else if (userAgent.includes('linux')) {
      setDetectedPlatform('linux');
    }
    
    // Fetch latest release
    fetchLatestRelease();
  }, []);

  const fetchLatestRelease = async () => {
    try {
      const response = await fetch(`${API}/desktop/latest-version`);
      const data = await response.json();
      if (data.available) {
        setLatestRelease(data);
      }
    } catch (error) {
      console.error('Failed to fetch latest release:', error);
    }
  };

  const handleDownload = (platform) => {
    // Get download URL from latest release
    if (!latestRelease?.download_urls) {
      alert('Downloads are not yet available. Please sign up and check back later!');
      return;
    }
    
    const urlKeyMap = {
      'windows': 'windows',
      'macos': 'macos-intel',
      'linux': 'linux'
    };
    
    const urlKey = urlKeyMap[platform.id];
    const downloadUrl = latestRelease.download_urls[urlKey];
    
    if (!downloadUrl) {
      alert(`Download for ${platform.name} is not available yet.`);
      return;
    }
    
    window.open(downloadUrl, '_blank');
  };

  // Sort platforms to show detected one first
  const sortedPlatforms = [...platforms].sort((a, b) => {
    if (a.id === detectedPlatform) return -1;
    if (b.id === detectedPlatform) return 1;
    return 0;
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a1628] via-[#0f2847] to-[#0a1628]">
      {/* Header */}
      <header className="border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Home
            </Button>
            <SyncLogoWithText variant="dark" />
          </div>
          <span className="text-sm text-gray-500">v{LATEST_VERSION}</span>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-12">
        {/* Hero */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#5A9BD5]/10 border border-[#5A9BD5]/20 rounded-full text-[#5A9BD5] text-sm mb-6">
            <Download className="w-4 h-4" />
            Desktop Application
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Trade Directly from Your Machine
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Run the SYNC bot locally for lower latency, better security, and direct exchange connections.
          </p>
        </div>

        {/* Features */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
          {features.map((feature) => (
            <div
              key={feature.title}
              className="bg-white/5 border border-white/10 rounded-xl p-4 text-center"
            >
              <feature.icon className="w-8 h-8 text-[#5A9BD5] mx-auto mb-3" />
              <h3 className="font-medium text-white mb-1">{feature.title}</h3>
              <p className="text-sm text-gray-400">{feature.description}</p>
            </div>
          ))}
        </div>

        {/* Download Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {sortedPlatforms.map((platform) => {
            const isDetected = platform.id === detectedPlatform;
            const Icon = platform.icon;
            const urlKeyMap = { 'windows': 'windows', 'macos': 'macos-intel', 'linux': 'linux' };
            const hasDownload = latestRelease?.download_urls?.[urlKeyMap[platform.id]];
            
            return (
              <div
                key={platform.id}
                className={`relative bg-white/5 border rounded-xl p-6 transition-all hover:border-[#5A9BD5]/50 ${
                  isDetected ? 'border-[#5A9BD5]/50 ring-1 ring-[#5A9BD5]/20' : 'border-white/10'
                }`}
              >
                {isDetected && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#5A9BD5] text-white text-xs font-medium rounded-full">
                    Recommended for you
                  </div>
                )}
                
                <div className="text-center mb-6">
                  <Icon className={`w-12 h-12 mx-auto mb-3 ${isDetected ? 'text-[#5A9BD5]' : 'text-gray-400'}`} />
                  <h3 className="text-xl font-semibold text-white">{platform.name}</h3>
                  <p className="text-sm text-gray-500 mt-1">{platform.requirements}</p>
                </div>

                <div className="space-y-3 mb-6 text-sm">
                  <div className="flex justify-between text-gray-400">
                    <span>Version</span>
                    <span className="text-white">{latestRelease?.version || 'Coming Soon'}</span>
                  </div>
                  <div className="flex justify-between text-gray-400">
                    <span>Format</span>
                    <span className="text-white">{platform.extension}</span>
                  </div>
                </div>

                <Button
                  onClick={() => handleDownload(platform)}
                  disabled={!hasDownload}
                  className={`w-full ${
                    !hasDownload
                      ? 'bg-gray-600 cursor-not-allowed opacity-50'
                      : isDetected
                        ? 'bg-[#5A9BD5] hover:bg-[#4A8BC5]'
                        : 'bg-white/10 hover:bg-white/20'
                  }`}
                >
                  <Download className="w-4 h-4 mr-2" />
                  {hasDownload ? `Download for ${platform.name}` : 'Coming Soon'}
                </Button>
              </div>
            );
          })}
        </div>

        {/* Installation Instructions */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-8">
          <h2 className="text-2xl font-semibold text-white mb-6">Installation</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h3 className="flex items-center gap-2 text-lg font-medium text-white mb-3">
                <Monitor className="w-5 h-5 text-[#5A9BD5]" />
                Windows
              </h3>
              <ol className="list-decimal list-inside space-y-2 text-gray-400 text-sm">
                <li>Download the .msi installer</li>
                <li>Double-click to run</li>
                <li>Follow the installation wizard</li>
                <li>Launch from Start Menu</li>
              </ol>
            </div>

            <div>
              <h3 className="flex items-center gap-2 text-lg font-medium text-white mb-3">
                <Apple className="w-5 h-5 text-[#5A9BD5]" />
                macOS
              </h3>
              <ol className="list-decimal list-inside space-y-2 text-gray-400 text-sm">
                <li>Download the .dmg file</li>
                <li>Open the downloaded file</li>
                <li>Drag SYNC to Applications</li>
                <li>Launch from Applications</li>
              </ol>
            </div>

            <div>
              <h3 className="flex items-center gap-2 text-lg font-medium text-white mb-3">
                <Terminal className="w-5 h-5 text-[#5A9BD5]" />
                Linux
              </h3>
              <ol className="list-decimal list-inside space-y-2 text-gray-400 text-sm">
                <li>Download the .AppImage file</li>
                <li>Make it executable: <code className="text-[#5A9BD5]">chmod +x *.AppImage</code></li>
                <li>Run: <code className="text-[#5A9BD5]">./SYNC*.AppImage</code></li>
              </ol>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-12">
          <h2 className="text-2xl font-semibold text-white mb-6">Frequently Asked Questions</h2>
          
          <div className="space-y-4">
            <div className="bg-white/5 border border-white/10 rounded-lg p-4">
              <h3 className="font-medium text-white mb-2">Do I need a separate account for the desktop app?</h3>
              <p className="text-gray-400 text-sm">No, use your existing SYNC account. Your credits and settings sync automatically.</p>
            </div>
            
            <div className="bg-white/5 border border-white/10 rounded-lg p-4">
              <h3 className="font-medium text-white mb-2">Are my API keys safe?</h3>
              <p className="text-gray-400 text-sm">Yes. API keys are encrypted using AES-256 and stored only on your machine. They're never sent to our servers.</p>
            </div>
            
            <div className="bg-white/5 border border-white/10 rounded-lg p-4">
              <h3 className="font-medium text-white mb-2">Can I use both web and desktop apps?</h3>
              <p className="text-gray-400 text-sm">Yes, but not simultaneously for the same trading pair. The desktop app is ideal for active trading, while the web app is great for monitoring and configuration.</p>
            </div>
            
            <div className="bg-white/5 border border-white/10 rounded-lg p-4">
              <h3 className="font-medium text-white mb-2">How do updates work?</h3>
              <p className="text-gray-400 text-sm">The app checks for updates automatically and prompts you to install them. You can also manually check from the Settings menu.</p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 mt-16">
        <div className="max-w-6xl mx-auto px-4 py-8 text-center text-gray-500 text-sm">
          <p>SYNC Desktop v{LATEST_VERSION}</p>
          <p className="mt-1">Windows • macOS • Linux</p>
        </div>
      </footer>
    </div>
  );
}
